// filepath: apps/web/src/features/proc/FailedInstanceDashboardPage.tsx
//
// FR-UI-PROC-1 AC-2 — page chrome for the failed-instance dashboard.
//
// Thin wrapper around <FailedInstanceDashboard />. Adds page chrome
// (title + subtitle). The page is tenant-scoped via TanStack Query
// cache keys (tenantSlug as the 2nd segment) — no `environmentKind`
// gate (failed-instance surfacing is all-environments per FR-OBS-6).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { FailedInstanceDashboard } from "./FailedInstanceDashboard";

export interface FailedInstanceDashboardPageProps {
  /**
   * Optional click-through to the trace viewer for an instance.
   * When supplied, the dashboard's "View trace" affordance
   * navigates to the trace page for the given instance id.
   * Typical implementation: window.history.pushState(...)
   * followed by a window.dispatchEvent("popstate") so the SPA
   * router re-evaluates the path.
   */
  onViewTrace?: (instanceId: string) => void;
}

export function FailedInstanceDashboardPage({
  onViewTrace,
}: FailedInstanceDashboardPageProps): React.ReactElement {
  const { t } = useTranslation();

  return (
    <main
      data-testid="failed-instance-dashboard-page"
      aria-labelledby="failed-instance-dashboard-page-title"
      className="failed-instance-dashboard-page"
    >
      <header className="failed-instance-dashboard-page-header">
        <h1
          id="failed-instance-dashboard-page-title"
          data-testid="failed-instance-dashboard-page-title"
          className="failed-instance-dashboard-page-title"
        >
          {t("processes.failed.title", "Failed instances")}
        </h1>
        <p className="failed-instance-dashboard-page-subtitle">
          {t(
            "processes.failed.subtitle",
            "Process instances in the failed state for this tenant. Retry a step or cancel the instance from each row.",
          )}
        </p>
      </header>
      <FailedInstanceDashboard onViewTrace={onViewTrace} />
    </main>
  );
}
